import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/common/Breadcrumb';
import CustomDropdown from '@/components/common/CustomDropdown';
import Card from '@/components/ui/cards/Card';
import CardHeader from '@/components/ui/cards/CardHeader';
import PerPageSelection from '@/components/ui/tables/PerPageSelection';
import validationErrors from '@/lib/validationErrors';
import { getItemService, deleteItemService } from '@/services/ItemServices';
import { cn } from '@/lib/utils';
import DataTableFilter from './component/DataTableFilter';
import DataTable from './component/DataTable';
import AddEditDrawer from './component/AddEditDrawer';
import PritntPdfTable from '@/components/common/PritntPdfTable';
import PermissionCheck from '@/components/common/PermissionCheck';

const ItemsPage = ({ pageTitle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { printTableRef, handleDownloadPdf } = PritntPdfTable('Items Data');

  const [dataSource, setDataSource] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [activeDrawer, setActiveDrawer] = useState(false);
  const [editDataId, setEditDataId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const filterDivRef = useRef(null);

  const [dataFilter, setDataFilter] = useState({
    search: '',
    page: 1,
    limit: 10,
    isSuperAdmin: 1,
    // ⬇️ Show items even if stock == 0 (so new perishable items appear right after create)
    extra_filter: { inStockOnly: false },
  });

  // Table columns
  const tableColumn = [
    { name: 'Name', isIncludePrint: true },
    { name: 'Category', isIncludePrint: true },
    { name: 'Perishable', isIncludePrint: true },
    { name: 'Price', isIncludePrint: true },
    { name: 'Status', isIncludePrint: true },
    { name: 'Action', isIncludePrint: false },
  ];

  useEffect(() => {
    getTableData();
  }, [dataFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDrawerOpen = (editId) => {
    if (editId && editId !== '') {
      setEditDataId(editId);
    } else {
      setEditDataId('');
    }
    setActiveDrawer(true);
    document.body.style.overflowY = 'hidden';
  };

  const handleView = (viewId) => {
    if (viewId) {
      navigate(`/admin/items/${viewId}`);
    }
  };

  // ── FIX: dedicated handlers for pagination ──
  const handlePageChange = (nextPage) => {
    setDataFilter((prev) => ({
      ...prev,
      page: Number(nextPage) || 1,
    }));
  };

  const handlePerPageChange = (a, b) => {
    const raw = typeof b !== 'undefined' ? b : a;
    const nextLimit = Number(raw) || 10;
    setDataFilter((prev) => ({
      ...prev,
      limit: nextLimit,
      page: 1, // reset to first page
    }));
  };

  // For filters/search
  const onChangeFilter = (name, value) => {
    setDataFilter({
      ...dataFilter,
      [name]: value,
      page: name === 'page' ? value : dataFilter.page,
    });
  };

  const handleFilterToggle = () => {
    if (filterDivRef.current) {
      if (!isActive) {
        setIsActive(true);
        filterDivRef.current.style.height = `100%`;
      } else {
        setIsActive(false);
        filterDivRef.current.style.height = '0px';
      }
    }
  };

  const getTableData = () => {
    setIsLoading(true);
    dispatch(getItemService(dataFilter))
      .then((res) => {
        setTotalRecord(res?.data?.data?.total);
        setDataSource(res?.data?.data?.data || []);
        setIsLoading(false);
      })
      .catch(({ message, errorData, statusCode }) => {
        validationErrors(errorData, message);
        setIsLoading(false);
      });
  };

  const deleteRecord = (deleteId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        dispatch(deleteItemService(deleteId))
          .then((res) => {
            Swal.fire({
              title: 'Deleted!',
              text: 'Your record has been deleted.',
              icon: 'success',
            });
            getTableData();
            setIsLoading(false);
          })
          .catch(({ message, errorData, statusCode }) => {
            validationErrors(errorData, message);
            setIsLoading(false);
          });
      }
    });
  };

  return (
    <>
      <Breadcrumb pageTitle={pageTitle} />
      <Card>
        <CardHeader>
          <h3 className="db-card-title">{pageTitle}</h3>
          <div className="db-card-filter">
            <div className="db-field-down-arrow">
              <PerPageSelection
                onChangeParPage={handlePerPageChange}
                limit={dataFilter.limit}
              />
            </div>
            <button
              className="db-card-filter-btn table-filter-btn"
              onClick={handleFilterToggle}
            >
              <i className="lab lab-filter lab-font-size-14"></i>
              <span>Filter</span>
            </button>
            <CustomDropdown
              buttonText={'Export'}
              buttonIcon={'lab-file-export'}
              dropdownList={[
                {
                  icon: 'lab-printer-line',
                  text: 'Print',
                  dropDownAction: handleDownloadPdf,
                },
              ]}
            />
            <PermissionCheck permission={'create_items'}>
              <button
                type="button"
                onClick={() => handleDrawerOpen('')}
                className={'db-btn h-[37px] text-white bg-primary'}
              >
                <i className={'lab lab-add-circle-line'}></i>
                Add Item
              </button>
            </PermissionCheck>
          </div>
        </CardHeader>
        <div
          className={cn('table-filter-div', isActive ? 'active' : '')}
          ref={filterDivRef}
        >
          <DataTableFilter onChangeFilter={onChangeFilter} />
        </div>
        <div ref={printTableRef}>
          <DataTable
            isLoading={isLoading}
            tableColumn={tableColumn}
            dataSource={dataSource}
            totalRecord={totalRecord}
            dataFilter={dataFilter}
            onPageChange={handlePageChange}   // ✅ real page change handler
            handleDrawerOpen={handleDrawerOpen}
            deleteRecord={deleteRecord}
            handleView={handleView}
          />
        </div>
        <AddEditDrawer
          titleText={pageTitle}
          getTableData={getTableData}
          activeDrawer={activeDrawer}
          setActiveDrawer={setActiveDrawer}
          editDataId={editDataId}
        />
      </Card>
    </>
  );
};

export default ItemsPage;
